import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { incrementEventCount, decrementEventCount } from './userRecordsService';
import { cleanupEventAlerts } from './eventAlertsService';
import { normalizeEmail, isValidEmail, computeInviteUpdate } from '../utils/eventInvite';
import { dedupeCanonicalEvents } from '../utils/eventDedup';

export interface Task {
  id: string;
  label: string;
  checked: boolean;
  date?: string;
}

export interface Event {
  id: string;
  title: string;
  date: Date | string;
  time: string;
  location?: string;
  description?: string;
  guests?: string[];
  recipes?: string[];
  inventoryItems?: string[];
  tasks?: Task[];
  userId: string;
  /** IDs de usuários Hostly convidados (têm acesso de leitura ao evento) */
  invitedUserIds?: string[];
  /** IDs de co-editores (podem editar logística e tarefas do evento) */
  editorUserIds?: string[];
  isAcceptedInvite?: boolean;
  originalEventId?: string;
  originalOwnerId?: string;
  pendingCollaboratorEmails?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  guestCount?: number;
  outfit?: string;
  photos?: string[];
  inventoryQuantities?: Record<string, number>;
  guestItems?: Record<string, string>; // guestId -> what they're bringing
  mesa?: { id: string; name: string; previewUrl: string };
  inventorySnapshot?: Array<{ id: string; name: string; photoUrl?: string; quantity?: number }>;
  recipesSnapshot?: Array<{ id: string; title: string; imageUrl?: string }>;
  sharedInventoryItems?: Array<{
    itemId: string;
    itemName: string;
    ownerId: string;
    ownerName: string;
    quantity?: number;
  }>;
}

export interface CreateEventOptions {
  /** Se true, não incrementa o contador de eventos (usado para eventos aceitos de convites) */
  isAcceptedInvite?: boolean;
  /** ID do evento original (do dono do convite) */
  originalEventId?: string;
  /** ID do dono do evento original */
  originalOwnerId?: string;
}

export const createEvent = async (
  userId: string,
  eventData: Omit<Event, 'id' | 'userId' | 'createdAt' | 'updatedAt'>,
  options?: CreateEventOptions
): Promise<string> => {
  try {
    const eventsRef = collection(db, 'events');

    // Corrigir timezone: se for string de data, parsear corretamente para não perder dia
    let dateToSave: Date;
    if (typeof eventData.date === 'string') {
      // Se for string no formato YYYY-MM-DD, criar data local sem conversão de timezone
      const dateStr = eventData.date;
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        dateToSave = new Date(year, month - 1, day); // Criar data local
      } else {
        dateToSave = new Date(eventData.date);
      }
    } else {
      dateToSave = eventData.date;
    }

    const newEvent: any = {
      ...eventData,
      date: Timestamp.fromDate(dateToSave),
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Se for evento aceito de convite, marcar como tal
    if (options?.isAcceptedInvite) {
      newEvent.isAcceptedInvite = true;
      if (options.originalEventId) {
        newEvent.originalEventId = options.originalEventId;
      }
      if (options.originalOwnerId) {
        newEvent.originalOwnerId = options.originalOwnerId;
      }
    }

    const docRef = await addDoc(eventsRef, newEvent);

    // Só incrementa o contador se NÃO for evento aceito de convite
    if (!options?.isAcceptedInvite) {
      await incrementEventCount(userId);
    }

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar evento:', error);
    throw error;
  }
};

export const getUserEvents = async (userId: string): Promise<Event[]> => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(
      eventsRef,
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const events: Event[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      events.push({
        id: doc.id,
        ...data,
        date: data.date?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Event);
    });

    return events;
  } catch (error) {
    console.error('Erro ao buscar eventos:', error);
    throw error;
  }
};

/**
 * Eventos em que o usuário é convidado (tem acesso de leitura via invitedUserIds).
 * Usa array-contains (índice de campo único, sem exigir índice composto) e
 * ordena em memória por data desc.
 */
export const getInvitedEvents = async (userId: string): Promise<Event[]> => {
  try {
    const eventsRef = collection(db, 'events');
    const q = query(eventsRef, where('invitedUserIds', 'array-contains', userId));
    const snapshot = await getDocs(q);
    const events: Event[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.userId === userId) return; // já vem em getUserEvents
      events.push({
        id: docSnap.id,
        ...data,
        date: data.date?.toDate ? data.date.toDate() : data.date,
        createdAt: data.createdAt?.toDate?.(),
        updatedAt: data.updatedAt?.toDate?.(),
      } as Event);
    });
    events.sort((a, b) => new Date(b.date as any).getTime() - new Date(a.date as any).getTime());
    return events;
  } catch (error) {
    console.error('Erro ao buscar eventos convidados:', error);
    return [];
  }
};

export const getUserAndInvitedEvents = async (userId: string): Promise<Event[]> => {
  const [own, invited] = await Promise.all([
    getUserEvents(userId),
    getInvitedEvents(userId),
  ]);
  return (dedupeCanonicalEvents([...own, ...invited]) as Event[]).sort(
    (a, b) => new Date(b.date as any).getTime() - new Date(a.date as any).getTime()
  );
};

export interface EventEditor {
  uid: string;
  name?: string;
  email?: string;
}

/** Busca um usuário Hostly por email (para adicionar como co-editor). */
export const findUserByEmail = async (email: string): Promise<EventEditor | null> => {
  const usersRef = collection(db, 'users');
  const snap = await getDocs(query(usersRef, where('email', '==', email.trim()), limit(1)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data();
  return { uid: d.id, name: data.name || data.displayName, email: data.email };
};

/** Adiciona um co-editor ao evento (também dá acesso de leitura). Só o dono deve chamar. */
export const addEventEditor = async (eventId: string, uid: string): Promise<void> => {
  await updateDoc(doc(db, 'events', eventId), {
    editorUserIds: arrayUnion(uid),
    invitedUserIds: arrayUnion(uid),
  });
};

/** Remove um co-editor do evento. */
export const removeEventEditor = async (eventId: string, uid: string): Promise<void> => {
  await updateDoc(doc(db, 'events', eventId), {
    editorUserIds: arrayRemove(uid),
  });
};

export type InviteByEmailResult =
  | { ok: true; user: EventEditor; alreadyHadAccess: boolean; promotedToEditor: boolean }
  | { ok: false; reason: 'invalid_email' | 'not_found' | 'is_owner' | 'invalid_target' };

/**
 * Compartilha o evento diretamente com um usuário Hostly pelo email.
 * Concede acesso de leitura (invitedUserIds) e, opcionalmente, de co-edição
 * (editorUserIds). Só o dono deve chamar. Não depende do casamento frágil
 * de guest↔conta: resolve a conta pelo email na hora.
 */
export const inviteUserToEventByEmail = async (
  event: Event,
  email: string,
  asEditor: boolean
): Promise<InviteByEmailResult> => {
  const normalized = normalizeEmail(email);
  if (!isValidEmail(normalized)) return { ok: false, reason: 'invalid_email' };

  const user = await findUserByEmail(normalized);
  if (!user) return { ok: false, reason: 'not_found' };

  const plan = computeInviteUpdate({
    ownerId: event.userId,
    targetUid: user.uid,
    currentInvited: event.invitedUserIds || [],
    currentEditors: event.editorUserIds || [],
    asEditor,
  });

  if (!plan.ok) return { ok: false, reason: plan.reason || 'invalid_target' };

  const updates: Record<string, unknown> = { invitedUserIds: arrayUnion(user.uid) };
  if (asEditor) updates.editorUserIds = arrayUnion(user.uid);
  await updateDoc(doc(db, 'events', event.id), updates);

  return { ok: true, user, alreadyHadAccess: plan.alreadyHadAccess, promotedToEditor: plan.promotedToEditor };
};

export const updateEvent = async (eventId: string, updates: Partial<Event>): Promise<void> => {
  try {
    const eventRef = doc(db, 'events', eventId);

    // Processar data se existir nos updates
    const processedUpdates: any = { ...updates };
    if (updates.date) {
      let dateToSave: Date;
      if (typeof updates.date === 'string') {
        const dateStr = updates.date;
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          dateToSave = new Date(dateStr + 'T12:00:00');
        } else {
          dateToSave = new Date(updates.date);
        }
      } else {
        dateToSave = updates.date;
      }
      processedUpdates.date = Timestamp.fromDate(dateToSave);
    }

    await updateDoc(eventRef, {
      ...processedUpdates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao atualizar evento:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId: string, userId: string): Promise<void> => {
  try {
    const eventRef = doc(db, 'events', eventId);

    // Verificar se é um evento aceito de convite antes de deletar
    const eventSnap = await getDoc(eventRef);
    const isAcceptedInvite = eventSnap.exists() && eventSnap.data()?.isAcceptedInvite === true;

    await deleteDoc(eventRef);

    // Só decrementa o contador se NÃO for evento aceito de convite
    if (!isAcceptedInvite) {
      await decrementEventCount(userId);
    }

    await cleanupEventAlerts(eventId);
  } catch (error) {
    console.error('Erro ao deletar evento:', error);
    throw error;
  }
};

export const getEvent = async (eventId: string): Promise<Event | null> => {
  try {
    const eventRef = doc(db, 'events', eventId);
    const eventDoc = await getDoc(eventRef);

    if (eventDoc.exists()) {
      const data = eventDoc.data();
      return {
        id: eventDoc.id,
        ...data,
        date: data.date?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Event;
    }

    return null;
  } catch (error) {
    console.error('Erro ao buscar evento:', error);
    throw error;
  }
};

export const subscribeToEvent = (
  eventId: string,
  callback: (event: Event | null) => void
) => {
  const eventRef = doc(db, 'events', eventId);
  return onSnapshot(eventRef, (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    const data = snap.data();
    callback({
      id: snap.id,
      ...data,
      date: data.date?.toDate?.() ?? data.date,
      createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
    } as Event);
  });
};